import React, { useContext, useEffect, useState } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  message,
  Card,
  Upload,
  Button,
  Modal,
  Tooltip,
  Select,
} from "antd";
import { useNavigate } from "react-router-dom";
import "dayjs/locale/en";
import { UploadFile } from "antd/lib/upload";
import { RcFile } from "antd/es/upload";
import { BiSolidInbox } from "react-icons/bi";
import { MdDeleteOutline } from "react-icons/md";
import { AuthContext } from "../../Auth/AuthContext";
import { addCustomer, getQualifiedRawData } from "../../utils/API";
import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import { Footer } from "antd/es/layout/layout";

const { Option } = Select;

interface TaskFormModalProps {
  visible: boolean;
  onClose: () => void;
  onTaskCreated: () => void;
}
const AddCustomerModal: React.FC<TaskFormModalProps> = ({
  visible,
  onClose,
  onTaskCreated,
}) => {
  const navigate = useNavigate();
  const { authData } = useContext(AuthContext);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const user_id = authData.user._id;
  const id = user_id.replace(/["']/g, "");
  const imageUrl = process.env.REACT_APP_Image_URL || "";
  const [country, setCountry] = useState([]);
  const [showAdminContact, setShowAdminContact] = useState(false);
  const roleId = authData.roleId;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [qualifiedLeads, setQualifiedLeads] = useState<any[]>([]);
  const [isTitlesLoading, setIsTitlesLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/uploads/country.json");
        if (!response.ok) {
          throw new Error("Failed to fetch");
        }
        const data = await response.json();
        setCountry(data); // Ensure data is set correctly
      } catch (error) {
        // console.error("Error fetching country data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      profilePhoto: fileList.length > 0 ? "uploaded" : undefined,
    });
  }, [fileList]);

  useEffect(() => {
    const fetchQualifiedLeads = async () => {
      if (!visible) {
        return;
      }

      try {
        setIsTitlesLoading(true);
        const response: any = await getQualifiedRawData();
        const leads = Array.isArray(response?.data) ? response.data : [];
        const uniqueLeads = leads.reduce((acc: any[], lead: any) => {
          const normalizedTitle =
            typeof lead?.title === "string" ? lead.title.trim() : "";

          if (
            normalizedTitle &&
            !acc.some(
              (item) =>
                item.title.toLowerCase() === normalizedTitle.toLowerCase(),
            )
          ) {
            acc.push({
              ...lead,
              title: normalizedTitle,
            });
          }

          return acc;
        }, []);

        setQualifiedLeads(uniqueLeads);
      } catch (error) {
        console.error("Error fetching qualified leads:", error);
        setQualifiedLeads([]);
      } finally {
        setIsTitlesLoading(false);
      }
    };

    fetchQualifiedLeads();
  }, [visible]);

  const handleTitleSelect = (_value: string, option: any) => {
    const selectedLead = option?.data;

    if (!selectedLead) {
      return;
    }

    form.setFieldsValue({
      name: selectedLead?.contactName || selectedLead?.clientName || "",
      mobile: selectedLead?.phone || "",
      companyName: selectedLead?.company || selectedLead?.clientName || "",
      email: selectedLead?.email || "",
    });
  };

  const getBase64 = (file: File | Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const imageProps = {
    name: "profilePhoto",
    multiple: false, // Only allow single file upload
    beforeUpload(file: RcFile) {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE; // Prevent upload
      }
      const isLt64M = file.size / 1024 / 1024 < 64;
      if (!isLt64M) {
        message.error("Image must be smaller than 64MB!");
        return Upload.LIST_IGNORE; // Prevent upload
      }
      return false; // Prevent automatic upload
    },
    onChange(info: any) {
      const newFileList = [...info.fileList];
      if (newFileList.length > 0) {
        const file = newFileList[0];
        if (!file.url && !file.preview) {
          getBase64(file.originFileObj).then((preview) => {
            file.preview = preview;
            setPreviewImage(preview);
          });
        } else {
          setPreviewImage(file.url || file.preview || "");
        }
      } else {
        setPreviewImage("");
      }
      setFileList(newFileList.slice(-1)); // Only keep the last uploaded file
    },
    fileList,
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);

      const formData = new FormData();

      // Append form fields
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key] || "");
      });

      // Append profile photo if available (now optional)
      if (fileList.length > 0) {
        const file = fileList[0].originFileObj; // Get the actual file object
        if (file) {
          formData.append("profilePhoto", file); // Append file with correct key
        }
      }
      // Removed the else block that was showing error for no photo

      // API Call to Store Customer
      const response = await addCustomer(formData);

      if (response) {
        message.success("Customer added successfully!");
        form.resetFields();
        setFileList([]);
        setPreviewImage("");
        onTaskCreated(); // Call the parent function to refresh the list
        setIsModalVisible(false); // Close modal on success
      } else {
        message.error("Failed to add customer. Please try again.");
      }
    } catch (error) {
      message.error("Something went wrong! Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      // CLEAR THE FORM DATA HERE
      form.resetFields();
      setFileList([]);
      setPreviewImage("");
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [visible, form]);

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={600}
        centered // Disable centered to use top positioning
        destroyOnClose
        className="custom-modal"
        bodyStyle={{
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Create New Customer
          </div>
        }
      >
        <Form
          onFinish={handleSubmit}
          form={form}
          layout="horizontal"
          // className=""
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 20 }}
          colon={false}
          style={{ margin: "0" }}
        >
          <div className="elements">
            <Form.Item
              colon={true}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              className="w-full"
            >
              <div className="flex justify-center">
                <div className="relative w-20 h-20">
                  <Upload.Dragger
                    {...imageProps}
                    className="w-full h-full rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-500 transition-colors bg-gray-50 overflow-hidden"
                    showUploadList={false}
                  >
                    {!previewImage ? (
                      <div className="flex flex-col items-center p-1">
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                          <UploadOutlined className="text-gray-400 text-base" />
                        </div>
                        <span className="text-xs text-gray-400 mt-1">
                          Profile
                        </span>
                      </div>
                    ) : (
                      <img
                        src={previewImage}
                        alt="Uploaded"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </Upload.Dragger>

                  {previewImage && (
                    <Button
                      type="text"
                      shape="circle"
                      size="small"
                      icon={<MdDeleteOutline size={16} />}
                      className="absolute top-0 right-0 bg-red-500 text-white hover:bg-red-600 shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage("");
                        setFileList([]);
                      }}
                    />
                  )}
                </div>
              </div>
            </Form.Item>

            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Title
                </span>
              }
            >
              <Select
                size="large"
                allowClear
                showSearch
                disabled={roleId == 2}
                loading={isTitlesLoading}
                placeholder="Select Title"
                className="CustomSelect"
                options={qualifiedLeads.map((item: any) => ({
                  label: item.title,
                  value: item._id,
                  data: item,
                }))}
                onChange={handleTitleSelect}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            {/* Name Field */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563", // Medium gray color similar to the image
                    fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                    fontSize: "14px", // Approximate font size based on the image
                    paddingLeft: "15px",
                    fontWeight: 500, // Medium weight to match "font-medium"
                  }}
                >
                  Name <span className="text-danger">*</span>
                </span>
              }
              name="name"
              required={false}
              rules={[{ required: true, message: "Please Enter Name" }]}
            >
              <Input
                size="large"
                disabled={roleId == 2}
                placeholder="Enter name"
                className="Allinput"
              />
            </Form.Item>

            {/* Mobile No. Field */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563", // Medium gray color similar to the image
                    fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                    fontSize: "14px", // Approximate font size based on the image
                    paddingLeft: "15px",
                    fontWeight: 500, // Medium weight to match "font-medium"
                  }}
                >
                  Mobile No. <span className="text-danger">*</span>
                </span>
              }
              name="mobile"
              required={false}
              rules={[
                { required: true, message: "Please Enter Mobile No." },
                // { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
              ]}
            >
              <Input
                size="large"
                disabled={roleId == 2}
                placeholder="Enter mobile number"
                className="Allinput"
              />
            </Form.Item>

            {/* Company Name Field */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563", // Medium gray color similar to the image
                    fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                    fontSize: "14px", // Approximate font size based on the image
                    paddingLeft: "15px",
                    fontWeight: 500, // Medium weight to match "font-medium"
                  }}
                >
                  Company Name <span className="text-danger">*</span>
                </span>
              }
              name="companyName"
              required={false}
              rules={[{ required: true, message: "Please Enter Company Name" }]}
            >
              <Input
                size="large"
                disabled={roleId == 2}
                placeholder="Enter company name"
                className="Allinput"
              />
            </Form.Item>

            {/* Email Field */}
            <Form.Item
              label={
                <span
                  style={{
                    color: "#4b5563", // Medium gray color similar to the image
                    fontFamily: "inter, sans-serif", // Matches the clean sans-serif style
                    fontSize: "14px", // Approximate font size based on the image
                    paddingLeft: "15px",
                    fontWeight: 500, // Medium weight to match "font-medium"
                  }}
                >
                  Email <span className="text-danger">*</span>
                </span>
              }
              name="email"
              required={false}
              rules={[
                { required: true, message: "Please Enter Email" },
                { type: "email", message: "Enter a valid email address" },
              ]}
            >
              <Input
                size="large"
                disabled={roleId == 2}
                placeholder="Enter email address"
                className="Allinput"
              />
            </Form.Item>
          </div>
          {/* Submit Button */}
          <Row justify="end">
            <Col
              xs={24}
              sm={24}
              md={24}
              xl={24}
              xxl={20}
              className="d-flex justify-content-end me-3 mt-3"
            >
              <div className="button-wrapper">
                <button
                  className="allcanclebutton"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button className="allsubmitebutton" disabled={isLoading}>
                  {isLoading ? "Add..." : "Add"}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AddCustomerModal;
